/**
 * `mod.sessions`: what a fill can see of your threads (RFC 0284 §4g).
 *
 * WHY THIS EXISTS. Asked whether somebody could write a header with tabs for
 * their sessions, the answer was half no: a fill could see the sessions
 * OPEN IN PANES, through `window.read()`, and could not see that it had any
 * others. `docs` lists documents and there was nothing for the other half of
 * what a person works on. The shipped sidebar lists sessions, so a replacement
 * that cannot is not a replacement.
 *
 * ACROSS THE WINDOW'S PROJECTS, not one. RFC 0290 lets a window hold several,
 * and a session list scoped to whichever project the focused pane happens to
 * sit on would change under a fill for a reason it cannot see. Each row
 * carries its own `projectId`, which is also what `window.open` requires.
 *
 * READ ONLY, like `docs`, and for the same reason. Opening one is
 * `window.open({ kind: 'agent', ... })`, which already exists and already ends
 * in `normalize`; a second door here would be the one that skips the rules.
 * Creating, sending and answering are S8c, behind a disclosed write grant.
 *
 * STATUS ARRIVED IN S8a, and the shape it arrived in is the reason it could.
 * "Which of these is waiting for me" is the thing a session list is FOR, and
 * computing it used to mean opening every row's log — which cost a renderer its
 * heap before the list finished drawing, and is a price no fill can be
 * handed. It is a registry field now, so a hundred rows cost one document read.
 *
 * ABSENT MEANS THE REGISTRY CANNOT TELL, and never means idle. A session no
 * runtime has reported on, or one whose claim went stale because its writer
 * died, has no status here. Drawing those as quiet is how a stuck session gets
 * skipped, so a fill should draw nothing rather than guess.
 */

/** One session, as a fill sees it. */
export interface SessionRow {
  id: string
  title: string
  /**
   * The project it belongs to.
   *
   * Carried for the reason `DocView.projectId` is: `window.open` refuses a
   * subject with no project, and a fill has no other way to learn one.
   * A window spanning projects makes this load-bearing rather than a
   * convenience.
   */
  projectId: string
  /** For a window with more than one project, so a list can say which. */
  projectName?: string
  updatedAt?: number
  /** Set when the session has been filed. A list that hides finished work
   *  needs to know which is which, and absent means still open. */
  completedAt?: number
  /**
   * What it is doing right now (RFC 0284 S8a).
   *
   * `asking` is the one worth drawing loudly: it means the session stopped and
   * is waiting for a person. `queued` and `running` differ by whether a worker
   * has picked it up, which is the difference between a slow agent and no
   * agent at all. Absent means the registry cannot tell.
   */
  status?: 'idle' | 'queued' | 'running' | 'asking'
}

export interface SessionsCapability {
  /** The window's sessions, most recently active first. */
  list(): SessionRow[]
  /** Fires when that list changes. Returns an unsubscribe. */
  onChange(fn: () => void): () => void
}

export interface SessionsDeps {
  sessions: () => SessionRow[]
  subscribe: (fn: () => void) => () => void
}

export function makeSessions(deps: SessionsDeps): SessionsCapability {
  return {
    // COPIED on the way out, like `docs.list`. The array the shell holds is
    // shared with the built-in chrome, and handing it over directly would let a
    // fill sort it in place and reorder somebody else's list with it.
    list: () => deps.sessions().map((s) => ({ ...s })),
    onChange: (fn) => deps.subscribe(fn),
  }
}
